import { useEffect, useRef, useState, type ReactNode } from 'react'
import { useParams } from 'react-router-dom'
import { api, type OutreachProspectDetail, type OutreachProspectState } from '@/api/client'
import { STATUS } from '@/lib/status'
import { formatFull } from '@/lib/time'
import { Button, StatusPill, buttonClass } from '@/ui'
import {
  CampaignHeader,
  ErrorNotice,
  LoadingCard,
  PlainHeader,
  errorMessage,
  isAbortError,
  isValidRouteID,
} from './shared'
import { ENDING_REASON, TerminalConfirm, type EndingKind } from './TerminalConfirm'

const KIND_LABEL: Record<string, string> = { connection: '연결 요청', praise: '칭찬', cta: '제안' }
const TONE_LABEL: Record<string, string> = { warm: '따뜻하게', concise: '간결하게', curious: '궁금해하며' }

const EVENT_LABEL: Record<string, string> = { imported: '가져옴', linkedin_verified: 'LinkedIn 주소 확인' }

/** An event named after a state reads as that state; the rest by a short name,
 *  and a type the console does not know shows as the server wrote it. */
function eventLabel(type: string): string {
  return EVENT_LABEL[type] ?? STATUS.prospect[type]?.label ?? type.replaceAll('_', ' ')
}

function Panel({
  title,
  description,
  aside,
  children,
}: {
  title: string
  description?: string
  aside?: ReactNode
  children: ReactNode
}) {
  return (
    <section aria-label={title} className="rounded-lg border border-border bg-card">
      <div className="flex items-start justify-between gap-3 border-b border-border px-4 py-3">
        <div className="min-w-0">
          <h2 className="text-section font-semibold">{title}</h2>
          {description && <p className="mt-0.5 text-meta text-muted-foreground">{description}</p>}
        </div>
        {aside}
      </div>
      <div className="p-4">{children}</div>
    </section>
  )
}

export default function OutreachProspect() {
  const prospectId = Number(useParams().prospectId)
  const campaignId = Number(useParams().campaignId)
  const validIDs = isValidRouteID(campaignId) && isValidRouteID(prospectId)
  const [detail, setDetail] = useState<OutreachProspectDetail | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [ending, setEnding] = useState<EndingKind | null>(null)
  const [loading, setLoading] = useState(validIDs)
  const [reloadKey, setReloadKey] = useState(0)
  const routeRef = useRef({ campaignId, prospectId })
  routeRef.current = { campaignId, prospectId }

  useEffect(() => {
    const controller = new AbortController()
    setDetail(null)
    setError(null)
    setBusy(false)
    if (!validIDs) {
      setLoading(false)
      return () => controller.abort()
    }
    setLoading(true)
    void api
      .outreachProspect(prospectId, controller.signal)
      .then((nextDetail) => {
        if (controller.signal.aborted) return
        if (
          nextDetail.prospect.id !== prospectId ||
          nextDetail.prospect.campaign_id !== campaignId
        ) {
          throw new Error('Outreach response did not match the current prospect route')
        }
        setDetail(nextDetail)
      })
      .catch((err: unknown) => {
        if (!isAbortError(err)) setError(errorMessage(err))
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false)
      })
    return () => controller.abort()
  }, [campaignId, prospectId, reloadKey, validIDs])

  const transition = async (to: OutreachProspectState, reason = '') => {
    if (!detail || detail.prospect.id !== prospectId || detail.prospect.campaign_id !== campaignId)
      return
    const actionRoute = { campaignId, prospectId }
    setBusy(true)
    setError(null)
    try {
      await api.transitionOutreachProspect(actionRoute.prospectId, to, reason)
      if (
        routeRef.current.campaignId === actionRoute.campaignId &&
        routeRef.current.prospectId === actionRoute.prospectId
      )
        setReloadKey((value) => value + 1)
    } catch (err) {
      if (
        routeRef.current.campaignId === actionRoute.campaignId &&
        routeRef.current.prospectId === actionRoute.prospectId
      )
        setError(errorMessage(err))
    } finally {
      if (
        routeRef.current.campaignId === actionRoute.campaignId &&
        routeRef.current.prospectId === actionRoute.prospectId
      )
        setBusy(false)
    }
  }

  if (!validIDs) {
    return (
      <div className="space-y-4">
        <PlainHeader title="메이커" description="캠페인이나 메이커 주소의 ID가 올바르지 않다." />
        <ErrorNotice message="올바르지 않은 아웃리치 캠페인 또는 메이커 ID다." />
      </div>
    )
  }

  const currentDetail =
    detail?.prospect.id === prospectId && detail.prospect.campaign_id === campaignId ? detail : null

  if (loading || (!error && !currentDetail)) {
    return (
      <div className="space-y-4">
        <PlainHeader title="메이커" description="메이커 정보를 불러오는 중…" />
        <LoadingCard>메이커를 불러오는 중…</LoadingCard>
      </div>
    )
  }

  if (error || !currentDetail) {
    return (
      <div className="space-y-4">
        <PlainHeader title="메이커" description="메이커를 불러오지 못했다." />
        <ErrorNotice message={error ?? '메이커를 찾을 수 없다.'} />
      </div>
    )
  }

  const { prospect, steps, events } = currentDetail
  const terminal = ['closed', 'skipped', 'do_not_contact', 'invalid_profile'].includes(
    prospect.state,
  )
  const linkedinOff = !prospect.linkedin_url || terminal || !currentDetail.contact_allowed
  return (
    <div className="space-y-4">
      <CampaignHeader
        campaignId={prospect.campaign_id}
        title={prospect.maker_name}
        description={`${prospect.product_name}의 ${prospect.maker_role || '메이커'}. 외부 프로필 방문과 모든 메시지는 직접 한다.`}
      />
      <ErrorNotice message={error} />
      {!currentDetail.contact_allowed && !terminal && (
        <div className="flex flex-col gap-3 rounded-lg border border-status-publishing/30 bg-status-publishing-soft/60 p-4 text-body sm:flex-row sm:items-center sm:justify-between">
          <span className="text-status-publishing">
            {currentDetail.safety_block_reason || '지금은 연락 동작을 쓸 수 없다.'}
          </span>
          {currentDetail.duplicate_confirmation_required && (
            <Button
              loading={busy}
              onClick={() =>
                void api
                  .reconfirmOutreachDuplicate(prospect.id)
                  .then(() => setReloadKey((value) => value + 1))
                  .catch((err: unknown) => setError(errorMessage(err)))
              }
            >
              중복 다시 확인
            </Button>
          )}
        </div>
      )}

      <div className="grid gap-4 lg:grid-cols-[minmax(0,1.1fr)_minmax(18rem,0.9fr)]">
        <div className="min-w-0 space-y-4">
          <Panel
            title="저장된 근거"
            description="가져온 제품 정보만이 정해진 문장 틀의 재료다."
            aside={<StatusPill domain="prospect" status={prospect.state} />}
          >
            <div className="space-y-4 text-body">
              <Evidence label="제품" value={prospect.product_name} />
              <Evidence label="한 줄 소개" value={prospect.product_tagline} />
              <Evidence label="설명" value={prospect.product_description} />
              <Evidence label="주제" value={prospect.product_topics.join(', ')} />
              <Evidence label="점수 이유" value={prospect.score_reason} />
              <div className="flex flex-wrap gap-2">
                <a
                  href={prospect.product_hunt_url}
                  target="_blank"
                  rel="noreferrer"
                  className={buttonClass('secondary', 'sm')}
                >
                  Product Hunt 열기
                </a>
                <a
                  href={linkedinOff ? undefined : prospect.linkedin_url}
                  target="_blank"
                  rel="noreferrer"
                  aria-disabled={linkedinOff || undefined}
                  className={buttonClass('secondary', 'sm')}
                >
                  LinkedIn 열기
                </a>
              </div>
            </div>
          </Panel>

          <Panel title="메시지 단계" description="서버가 확정한 문장, 고른 근거, 승인 상태다. 여기서는 읽기만 한다.">
            <div className="space-y-3">
              {steps.length === 0 && <p className="text-body text-muted-foreground">준비된 메시지가 없다.</p>}
              {steps.map((step) => (
                <section key={step.id} className="rounded-lg border border-border bg-background p-3">
                  <div className="mb-2 flex items-center justify-between gap-2">
                    <h3 className="text-body font-semibold">{KIND_LABEL[step.kind] ?? step.kind}</h3>
                    <StatusPill domain="step" status={step.status} />
                  </div>
                  <p className="whitespace-pre-wrap rounded-md bg-muted/50 p-3 text-body leading-6">
                    {step.body_text}
                  </p>
                  <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-meta text-muted-foreground">
                    <span>근거: {step.evidence_ids.join(', ')}</span>
                    <span>말투: {TONE_LABEL[step.tone] ?? step.tone}</span>
                    <span>수정 {step.edits_count}회</span>
                  </div>
                </section>
              ))}
            </div>
          </Panel>
        </div>

        <div className="min-w-0 space-y-4">
          <Panel title="직접 기록하는 단계" description="앱 밖에서 일어난 일만 기록한다.">
            <div className="space-y-3">
              <Button
                variant="primary"
                className="w-full"
                loading={busy}
                disabled={prospect.state !== 'connection_sent'}
                onClick={() => void transition('connected')}
              >
                연결됨으로 표시
              </Button>
              <Button
                variant="primary"
                className="w-full"
                loading={busy}
                disabled={prospect.state !== 'praise_sent'}
                onClick={() => void transition('replied')}
              >
                답장 옴으로 표시
              </Button>
              <Button
                className="w-full"
                loading={busy}
                disabled={terminal}
                onClick={() => setEnding('invalid_profile')}
              >
                프로필 무효
              </Button>
              <Button
                variant="dangerOutline"
                className="w-full"
                loading={busy}
                disabled={terminal}
                onClick={() => setEnding('do_not_contact')}
              >
                연락 금지
              </Button>
              <Button
                variant="primary"
                className="w-full"
                loading={busy}
                disabled={prospect.state !== 'cta_sent'}
                onClick={() => setEnding('closed')}
              >
                종료로 기록
              </Button>
              <p className="text-meta text-muted-foreground">
                제안은 답장을 기록한 뒤에만 준비할 수 있다. 클릭이나 LinkedIn 활동으로 답장을 짐작하지 않는다.
                종료는 제안을 보낸 뒤에만 기록할 수 있다.
              </p>
              <TerminalConfirm
                kind={ending}
                maker={prospect.maker_name}
                onCancel={() => setEnding(null)}
                onConfirm={async (kind) => {
                  setEnding(null)
                  if (kind === 'closed') await transition('closed')
                  else if (kind === 'do_not_contact' || kind === 'invalid_profile')
                    await transition(kind, ENDING_REASON[kind])
                }}
              />
            </div>
          </Panel>

          <Panel title="이력">
            {events.length === 0 ? (
              <p className="text-body text-muted-foreground">기록된 이력이 없다.</p>
            ) : (
              <ol className="relative space-y-4 border-l border-border pl-4">
                {events.map((event) => (
                  <li key={event.id}>
                    <div className="text-body font-medium">{eventLabel(event.event_type)}</div>
                    <time dateTime={event.created_at} className="text-meta text-muted-foreground">
                      {formatFull(event.created_at)}
                    </time>
                    {Object.keys(event.metadata ?? {}).length > 0 && (
                      <div className="mt-1 break-words text-meta text-muted-foreground">
                        {metadataSummary(event.metadata)}
                      </div>
                    )}
                  </li>
                ))}
              </ol>
            )}
          </Panel>
        </div>
      </div>
    </div>
  )
}

function Evidence({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-meta font-medium text-muted-foreground">{label}</div>
      <div className="mt-1 whitespace-pre-wrap leading-6">{value || '정보 없음'}</div>
    </div>
  )
}

function metadataSummary(metadata: Record<string, unknown>): string {
  return Object.entries(metadata)
    .map(([key, value]) => `${key}: ${String(value)}`)
    .join(' · ')
}
